import * as THREE from "three";
import {
  humanoidAnimationIssues,
  type HumanoidAnimationAssetV1,
  type HumanoidAnimationEvent,
  type HumanoidInterpolation,
  type HumanoidScalarKey,
  type HumanoidVectorKey,
} from "@hyperlight/shared";

export interface LoadedHumanoidAnimation {
  asset: HumanoidAnimationAssetV1;
  clip: THREE.AnimationClip;
}

const assetCache = new Map<string, Promise<LoadedHumanoidAnimation>>();

function interpolation(mode: HumanoidInterpolation | undefined): THREE.InterpolationModes {
  return mode === "step" ? THREE.InterpolateDiscrete : THREE.InterpolateLinear;
}

function modeOf(keys: readonly { interpolation?: HumanoidInterpolation }[]): THREE.InterpolationModes {
  return interpolation(keys[0]?.interpolation);
}

/** Compile editable source data into the exact Three.js tracks used by Avatar.
 * Events and constraints deliberately stay on the source asset. */
export function compileHumanoidAnimation(asset: HumanoidAnimationAssetV1): THREE.AnimationClip {
  const issues = humanoidAnimationIssues(asset);
  if (issues.length) throw new Error(`Invalid humanoid animation "${asset.id}": ${issues.join("; ")}`);
  const tracks: THREE.KeyframeTrack[] = [];
  for (const [bone, source] of Object.entries(asset.boneTracks)) {
    if (source.rotation?.length) {
      const values = source.rotation.flatMap((key) => key.value);
      tracks.push(new THREE.QuaternionKeyframeTrack(
        `.bones[${bone}].quaternion`,
        source.rotation.map((key) => key.time),
        values,
        modeOf(source.rotation),
      ));
    }
    if (source.position?.length) {
      const values = source.position.flatMap((key) => key.value);
      tracks.push(new THREE.VectorKeyframeTrack(
        `.bones[${bone}].position`,
        source.position.map((key) => key.time),
        values,
        modeOf(source.position),
      ));
    }
  }
  const clip = new THREE.AnimationClip(asset.id, asset.duration, tracks);
  return clip;
}

export function emptyHumanoidAnimation(id = "new-animation"): HumanoidAnimationAssetV1 {
  return {
    version: 1,
    id,
    duration: 1,
    loop: false,
    sampleRate: 30,
    rootMode: "locked",
    tags: [],
    boneTracks: {},
    events: [],
    constraints: [],
    previewContexts: [],
  };
}

export function cloneHumanoidAnimation(asset: HumanoidAnimationAssetV1): HumanoidAnimationAssetV1 {
  return JSON.parse(JSON.stringify(asset)) as HumanoidAnimationAssetV1;
}

/** Production ingestion boundary for source-controlled .hlanim.json assets. */
export function loadHumanoidAnimation(url: string): Promise<LoadedHumanoidAnimation> {
  let pending = assetCache.get(url);
  if (pending) return pending;
  pending = fetch(url).then(async (response) => {
    if (!response.ok) throw new Error(`Could not load humanoid animation ${url}: HTTP ${response.status}`);
    const value = await response.json() as unknown;
    const issues = humanoidAnimationIssues(value);
    if (issues.length) throw new Error(`Invalid humanoid animation ${url}: ${issues.join("; ")}`);
    const asset = value as HumanoidAnimationAssetV1;
    return { asset, clip: compileHumanoidAnimation(asset) };
  }).catch((error) => {
    assetCache.delete(url);
    throw error;
  });
  assetCache.set(url, pending);
  return pending;
}

/** Events crossed by a forward-moving playhead. A wrapped interval is split at
 * duration so loop events fire once and in playback order. */
export function humanoidAnimationEventsBetween(
  asset: HumanoidAnimationAssetV1,
  previousTime: number,
  currentTime: number,
  wrapped = false,
): HumanoidAnimationEvent[] {
  const events = asset.events ?? [];
  if (!wrapped) return events.filter((event) => event.time > previousTime && event.time <= currentTime);
  return [
    ...events.filter((event) => event.time > previousTime && event.time <= asset.duration),
    ...events.filter((event) => event.time >= 0 && event.time <= currentTime),
  ];
}

/** Sample a constraint weight independently of Three.js so gameplay can decide
 * when to bind/release an IK contact without inspecting render bones. */
export function humanoidConstraintWeight(keys: readonly HumanoidScalarKey[], time: number): number {
  if (!keys.length) return 0;
  if (time <= keys[0].time) return keys[0].value;
  for (let index = 1; index < keys.length; index++) {
    const right = keys[index], left = keys[index - 1];
    if (time > right.time) continue;
    if (left.interpolation === "step") return left.value;
    const t = (time - left.time) / Math.max(1e-6, right.time - left.time);
    return THREE.MathUtils.lerp(left.value, right.value, t);
  }
  return keys[keys.length - 1].value;
}

export function humanoidVectorTrackValue(keys: readonly HumanoidVectorKey[], time: number, out: THREE.Vector3): THREE.Vector3 {
  if (!keys.length) return out.set(0, 0, 0);
  if (time <= keys[0].time) return out.fromArray(keys[0].value);
  for (let index = 1; index < keys.length; index++) {
    const right = keys[index], left = keys[index - 1];
    if (time > right.time) continue;
    out.fromArray(left.value);
    if (left.interpolation === "step") return out;
    const t = (time - left.time) / Math.max(1e-6, right.time - left.time);
    return out.lerp(new THREE.Vector3().fromArray(right.value), t);
  }
  return out.fromArray(keys[keys.length - 1].value);
}

export interface SampledHumanoidConstraint {
  id: string;
  effector: string;
  target: string;
  targetPosition: THREE.Vector3;
  pole?: readonly [number, number, number];
  positionWeight: number;
  rotationWeight: number;
}

/** Resolve render-independent constraint curves. `targetPosition` remains in
 * the declared target frame; the interaction supplies the world transform. */
export function sampleHumanoidConstraints(
  asset: HumanoidAnimationAssetV1,
  time: number,
): SampledHumanoidConstraint[] {
  return (asset.constraints ?? []).map((constraint) => ({
    id: constraint.id,
    effector: constraint.effector,
    target: constraint.target,
    targetPosition: humanoidVectorTrackValue(constraint.targetPosition ?? [], time, new THREE.Vector3()),
    pole: constraint.pole,
    positionWeight: humanoidConstraintWeight(constraint.positionWeight, time),
    rotationWeight: humanoidConstraintWeight(constraint.rotationWeight ?? [], time),
  }));
}
